import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { isSafeHttpUrl } from '../../../services/shortcuts/shortcutUrl';
import {
  askAskSb,
  cancelAskSb,
  fetchAskSbPreview,
  getAskSbAccess,
  openAskSbOriginal,
  searchAskSb,
} from '../../../services/askSb/askSbApi';
import {
  clearAskSbConversation,
  clearAskSbOnRevisionMismatch,
  readAskSbConversation,
  syncAskSbIdentity,
  writeAskSbConversation,
} from '../../../services/askSb/conversationMemory';

const PHASE_LABEL = {
  answering: 'Answering',
};

const HISTORY_TURNS = 8;
const HISTORY_TEXT = 4000;
const QUESTION_LIMIT = 2000;

const FILE_TYPES = {
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.google-apps.document': 'Google Doc',
  'text/plain': 'Text',
};

function fileTypeLabel(mime) {
  const value = String(mime || '');
  if (FILE_TYPES[value]) return FILE_TYPES[value];
  if (value.startsWith('text/')) return 'Text';
  return value || 'File';
}

function canSearchAccess(access) {
  if (!access) return false;
  if (access.canSearch === true || access.canConsume === true) return true;
  const reasons = Array.isArray(access.setupReasons) ? access.setupReasons : [];
  return access.authorized === true && !reasons.includes('drive') && reasons.includes('openai');
}

function clip(value, max) {
  return String(value || '').slice(0, max);
}

function historyForAsk(turns) {
  return (turns || []).slice(-HISTORY_TURNS).map((turn) => {
    const role = turn.role === 'assistant' ? 'assistant' : 'user';
    const item = { role, text: clip(turn.text, HISTORY_TEXT) };
    if (role === 'assistant' && Array.isArray(turn.sourceHandles) && turn.sourceHandles.length) {
      item.sourceHandles = turn.sourceHandles.map(String).filter(Boolean);
    }
    return item;
  });
}

function OriginalPreview({ blob }) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!blob) return undefined;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
    };
  }, [blob]);

  if (!url) return null;
  return (
    <Box
      component="iframe"
      data-testid="ask-sb-preview-frame"
      title="Original policy preview"
      src={url}
      sandbox="allow-same-origin"
      referrerPolicy="no-referrer"
      sx={{
        width: '100%',
        minHeight: 320,
        height: '70vh',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
      }}
    />
  );
}

function PreviewBody({ preview }) {
  if (!preview) return null;
  if (preview.kind === 'extracted_text') {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          data-testid="ask-sb-extracted-label"
          sx={{ fontWeight: 700, mb: 1 }}
        >
          Extracted text, not the original file
        </Typography>
        <Box
          component="pre"
          data-testid="ask-sb-extracted-text"
          sx={{
            m: 0,
            p: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'inherit',
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: 'divider',
            maxHeight: '70vh',
            overflow: 'auto',
          }}
        >
          {preview.text}
        </Box>
      </Box>
    );
  }
  if (preview.kind === 'original') {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
          Original PDF
        </Typography>
        <OriginalPreview blob={preview.blob} />
      </Box>
    );
  }
  return null;
}

export default function AskSbPanel() {
  const { currentUser, tenantId } = useAuth();
  const uid = currentUser?.uid || '';
  const [access, setAccess] = useState(null);
  const [mode, setMode] = useState('ask');
  const [question, setQuestion] = useState('');
  const [keyword, setKeyword] = useState('');
  const [searchPhase, setSearchPhase] = useState('idle');
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [runId, setRunId] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [serverRevision, setServerRevision] = useState(null);
  const revisionRef = useRef(null);
  const abortRef = useRef(null);
  const generationRef = useRef(0);
  const lastQuestionRef = useRef('');
  const lastKeywordRef = useRef('');
  const requestIdRef = useRef('');
  const searchGenerationRef = useRef(0);
  const modeTouchedRef = useRef(false);

  const busy = phase === 'answering';

  useEffect(() => {
    revisionRef.current = serverRevision;
  }, [serverRevision]);

  useEffect(() => {
    syncAskSbIdentity(uid, tenantId);
    const stored = readAskSbConversation(uid, tenantId);
    setMessages(stored.messages || []);
    if (stored.revision != null) setServerRevision(stored.revision);
  }, [uid, tenantId]);

  useEffect(() => {
    if (!uid || !tenantId) return undefined;
    let cancelled = false;
    getAskSbAccess()
      .then((nextAccess) => {
        if (cancelled) return;
        setAccess(nextAccess || null);
        const revision = nextAccess?.revision;
        if (revision != null && clearAskSbOnRevisionMismatch(uid, tenantId, revision)) {
          setMessages([]);
        }
        if (revision != null) setServerRevision(revision);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Ask Signal Bridges is unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [uid, tenantId]);

  useEffect(() => {
    if (modeTouchedRef.current || !access) return;
    if (access.canConsume !== true && canSearchAccess(access)) setMode('find');
  }, [access]);

  useEffect(() => {
    if (!busy) return undefined;
    const started = Date.now();
    setElapsed(0);
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - started) / 1000));
    }, 250);
    return () => clearInterval(timer);
  }, [busy, runId]);

  function remember(next) {
    writeAskSbConversation(uid, tenantId, next);
    setMessages(next.messages || []);
  }

  function applyMismatch(err) {
    if (err?.detail !== 'ask_sb_revision_mismatch') return false;
    clearAskSbConversation(uid, tenantId);
    setMessages([]);
    setPreview(null);
    return true;
  }

  function chooseMode(next) {
    modeTouchedRef.current = true;
    setMode(next);
  }

  async function runSearch(raw) {
    const text = clip(raw, QUESTION_LIMIT).trim();
    if (!text || searchPhase === 'searching') return;
    const generation = searchGenerationRef.current + 1;
    searchGenerationRef.current = generation;
    lastKeywordRef.current = text;
    setSearchError('');
    setSearchResults([]);
    setPreview(null);
    setPreviewError('');
    setSearchPhase('searching');
    try {
      const data = await searchAskSb(text);
      if (searchGenerationRef.current !== generation) return;
      const sources = Array.isArray(data?.sources) ? data.sources : [];
      setSearchResults(sources);
      setSearchPhase(sources.length ? 'results' : 'empty');
    } catch (err) {
      if (searchGenerationRef.current !== generation) return;
      const status = Number(err?.status || 0);
      setSearchError(!status || status >= 500
        ? 'Could not reach the policy folder.'
        : (err?.message || 'Search failed'));
      setSearchPhase('error');
    }
  }

  async function loadPreview(sourceHandle) {
    setPreviewError('');
    setPreview(null);
    setPreviewOpen(true);
    try {
      const result = await fetchAskSbPreview(sourceHandle);
      setPreview(result);
    } catch (err) {
      applyMismatch(err);
      setPreviewError(err?.message || 'Preview failed');
    }
  }

  async function askQuestion(raw) {
    const text = clip(raw, QUESTION_LIMIT).trim();
    if (!text || !uid || !tenantId || busy || revisionRef.current == null) return;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const controller = new AbortController();
    abortRef.current = controller;
    lastQuestionRef.current = text;
    setError('');
    setPreview(null);
    setPreviewError('');
    setRunId((value) => value + 1);
    const stored = readAskSbConversation(uid, tenantId);
    const history = historyForAsk(stored.turns);
    const clientRequestId = (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID())
      || `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    requestIdRef.current = clientRequestId;

    try {
      setPhase('answering');
      const body = {
        question: text,
        clientRequestId,
        history,
      };
      if (revisionRef.current != null) body.revision = revisionRef.current;
      const data = await askAskSb(body, { signal: controller.signal });
      if (generationRef.current !== generation) return;

      const sources = Array.isArray(data?.sources) ? data.sources : [];
      const answer = clip(data?.answer, HISTORY_TEXT);
      const handles = sources.map((source) => source?.sourceHandle).filter(Boolean);
      const userTurn = { role: 'user', text };
      const assistantTurn = {
        role: 'assistant',
        text: answer,
        ...(handles.length ? { sourceHandles: handles } : {}),
      };
      const nextMessages = [
        ...(stored.messages || []),
        { id: `${clientRequestId}-q`, role: 'user', text },
        {
          id: `${clientRequestId}-a`,
          role: 'assistant',
          text: answer,
          sources,
          insufficient: Boolean(data?.insufficient),
          live: data?.usage?.live === true,
        },
      ].slice(-HISTORY_TURNS);
      const nextTurns = [...(stored.turns || []), userTurn, assistantTurn].slice(-HISTORY_TURNS);
      const revision = data?.revision ?? revisionRef.current;
      remember({ revision, turns: nextTurns, messages: nextMessages });
      if (revision != null) setServerRevision(revision);
      if (!answer && sources.length === 0) {
        setPhase('no-results');
      } else {
        setPhase('answer');
      }
      const hint = data?.previewHint?.sourceHandle;
      if (hint) loadPreview(hint);
    } catch (err) {
      if (generationRef.current !== generation) return;
      if (err?.name === 'AbortError' || controller.signal.aborted) {
        setPhase('cancelled');
        return;
      }
      applyMismatch(err);
      setError(err?.message || 'Ask Signal Bridges request failed');
      setPhase('error');
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  function cancel() {
    const requestId = requestIdRef.current;
    requestIdRef.current = '';
    generationRef.current += 1;
    abortRef.current?.abort();
    if (requestId) {
      cancelAskSb(requestId).catch(() => {});
    }
    setPhase('cancelled');
  }

  function newConversation() {
    cancel();
    clearAskSbConversation(uid, tenantId);
    setMessages([]);
    setQuestion('');
    setPreview(null);
    setPreviewError('');
    setError('');
    setPhase('idle');
  }

  async function openOriginal(url) {
    try {
      await openAskSbOriginal(url);
    } catch (err) {
      setError(err?.message || 'Could not open the original');
    }
  }

  return (
    <Box
      data-testid="ask-sb-panel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        width: '100%',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, gap: 1 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Ask Signal Bridges
        </Typography>
        <Button
          size="small"
          data-testid="ask-sb-new-conversation"
          onClick={newConversation}
        >
          New conversation
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button
          size="small"
          variant={mode === 'find' ? 'contained' : 'outlined'}
          aria-pressed={mode === 'find'}
          data-testid="ask-sb-mode-find"
          onClick={() => chooseMode('find')}
        >
          Find a policy
        </Button>
        <Button
          size="small"
          variant={mode === 'ask' ? 'contained' : 'outlined'}
          aria-pressed={mode === 'ask'}
          data-testid="ask-sb-mode-ask"
          onClick={() => chooseMode('ask')}
        >
          Ask SB
        </Button>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
        {mode === 'find' ? (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" data-testid="ask-sb-keyword-note" color="text.secondary">
              Keyword search in the approved folder. Subfolders are included only when that setting is on.
              It matches words of at least 4 letters in the document title or its text. This is not an AI search.
            </Typography>
            {searchPhase === 'searching' ? (
              <Typography variant="body2" data-testid="ask-sb-search-status" sx={{ mt: 1, fontWeight: 700 }}>
                Searching
              </Typography>
            ) : null}
            {searchPhase === 'empty' ? (
              <Typography variant="body2" data-testid="ask-sb-search-empty" color="text.secondary" sx={{ mt: 1 }}>
                No matching documents
              </Typography>
            ) : null}
            {searchPhase === 'error' ? (
              <Box sx={{ mt: 1 }}>
                <Alert severity="error" data-testid="ask-sb-search-error">{searchError}</Alert>
                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  data-testid="ask-sb-search-retry"
                  onClick={() => runSearch(lastKeywordRef.current)}
                >
                  Retry
                </Button>
              </Box>
            ) : null}
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {searchResults.map((source) => (
                <Stack
                  key={source.sourceHandle || source.title}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    data-testid="ask-sb-search-result"
                    onClick={() => loadPreview(source.sourceHandle)}
                  >
                    {source.title || 'Policy'}
                  </Button>
                  <Typography variant="caption" data-testid="ask-sb-file-type">
                    {fileTypeLabel(source.mimeType)}
                  </Typography>
                  {isSafeHttpUrl(source.sourceUrl) ? (
                    <Button
                      size="small"
                      data-testid="ask-sb-open-original"
                      onClick={() => openOriginal(source.sourceUrl)}
                    >
                      Open original
                    </Button>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : null}
        {mode === 'ask' && access && access.canConsume !== true ? (
          <Box data-testid="ask-sb-ask-setup" sx={{ minWidth: 0 }}>
            <Typography variant="body2">
              Questions need model gpt-4.1 and a saved API key. Keyword search does not.
            </Typography>
            {access.canConfigure ? (
              <Button
                component="a"
                href="/admin/integrations"
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
              >
                Open Integrations
              </Button>
            ) : (
              <Typography variant="body2" sx={{ mt: 1 }}>
                A tenant administrator needs to finish this in Integrations.
              </Typography>
            )}
          </Box>
        ) : null}
        {mode === 'ask' ? messages.map((message) => (
          <Box key={message.id} sx={{ mb: 1.5, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {message.role === 'assistant' ? 'Answer' : 'You'}
            </Typography>
            <Typography
              variant="body2"
              data-testid={message.role === 'assistant' ? 'ask-sb-answer' : undefined}
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {message.text}
            </Typography>
            {message.role === 'assistant' && message.live !== true ? (
              <Typography variant="caption" data-testid="ask-sb-not-live" color="text.secondary">
                Not a live Drive or OpenAI result
              </Typography>
            ) : null}
            {message.insufficient ? (
              <Typography variant="caption" display="block" color="text.secondary">
                The sources did not include enough information.
              </Typography>
            ) : null}
            {Array.isArray(message.sources) && message.sources.length > 0 ? (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {message.sources.map((source) => (
                  <Stack
                    key={source.sourceHandle || source.title}
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => loadPreview(source.sourceHandle)}
                    >
                      {source.title || 'Policy'}
                    </Button>
                    {isSafeHttpUrl(source.sourceUrl) ? (
                      <Button
                        size="small"
                        data-testid="ask-sb-open-original"
                        onClick={() => openOriginal(source.sourceUrl)}
                      >
                        Open original
                      </Button>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Box>
        )) : null}

        {mode === 'ask' && phase === 'no-results' ? (
          <Typography variant="body2" data-testid="ask-sb-no-results" color="text.secondary">
            No results
          </Typography>
        ) : null}
        {mode === 'ask' && PHASE_LABEL[phase] ? (
          <Typography variant="body2" data-testid="ask-sb-phase" sx={{ fontWeight: 700 }}>
            {PHASE_LABEL[phase]}
          </Typography>
        ) : null}
        {mode === 'ask' && busy ? (
          <Typography variant="caption" data-testid="ask-sb-elapsed" color="text.secondary">
            Elapsed {elapsed}s
          </Typography>
        ) : null}
        {mode === 'ask' && error ? (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="ask-sb-error">
            {error}
          </Alert>
        ) : null}
        {mode === 'ask' && phase === 'error' ? (
          <Button
            size="small"
            sx={{ mt: 1 }}
            data-testid="ask-sb-retry"
            onClick={() => askQuestion(lastQuestionRef.current)}
          >
            Retry
          </Button>
        ) : null}
        {previewError ? (
          <Alert severity="error" sx={{ mt: 1 }}>{previewError}</Alert>
        ) : null}
        {preview ? (
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setPreviewOpen((open) => !open)}>
              {previewOpen ? 'Hide preview' : 'Show preview'}
            </Button>
            {previewOpen ? <Box sx={{ mt: 1 }}><PreviewBody preview={preview} /></Box> : null}
          </Box>
        ) : null}
      </Box>

      {mode === 'find' ? (
        <Box
          component="form"
          sx={{ pt: 1, display: 'flex', gap: 1, alignItems: 'flex-end', minWidth: 0 }}
          onSubmit={(event) => {
            event.preventDefault();
            runSearch(keyword);
          }}
        >
          <TextField
            fullWidth
            size="small"
            name="keyword"
            data-testid="ask-sb-keyword"
            placeholder="Keyword"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value.slice(0, QUESTION_LIMIT))}
            inputProps={{ maxLength: QUESTION_LIMIT, 'aria-label': 'Policy keyword' }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!keyword.trim() || searchPhase === 'searching'}
          >
            Search
          </Button>
        </Box>
      ) : null}
      {mode === 'ask' && (!access || access.canConsume === true) ? (
        <Box
          component="form"
          sx={{ pt: 1, display: 'flex', gap: 1, alignItems: 'flex-end', minWidth: 0 }}
          onSubmit={(event) => {
            event.preventDefault();
            askQuestion(question);
            setQuestion('');
          }}
        >
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            name="question"
            data-testid="ask-sb-question"
            placeholder="Ask a question"
            value={question}
            onChange={(event) => setQuestion(event.target.value.slice(0, QUESTION_LIMIT))}
            inputProps={{ maxLength: QUESTION_LIMIT, 'aria-label': 'Ask a question' }}
          />
          {busy ? (
            <Button type="button" data-testid="ask-sb-cancel" onClick={cancel}>
              Cancel
            </Button>
          ) : (
            <Button type="submit" variant="contained" disabled={!question.trim() || serverRevision == null}>
              Ask
            </Button>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
